import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { NilmService } from '../../../services/nilm.service';
import { ProfileUpdateService } from '../../../services/profile-update.service';
import { AppliancesDetailDataProviderService } from '../../../services/data-provider/appliances-detail-data-provider.service';
import { AppliancesDetailDataMode } from '../../../shared/enums/appliances-detail-data-mode.enum';
import { Subscription, Subject, tap } from 'rxjs';
import { AnimationOptions } from 'ngx-lottie';
import { map } from 'rxjs/operators';
import * as moment from 'moment/moment';
import { ViewState } from '../../../shared/enums/view-state.enum';
import { translateAppliance } from '../../../tiles/appliances/appliances.utils';
import { InitializationService } from '../../../services/initialization.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-appliance-detail-data-display',
    templateUrl: './appliance-detail-data-display.component.html',
    styleUrls: ['./appliance-detail-data-display.component.scss'],
})
export class ApplianceDetailDataDisplayComponent implements OnInit, OnDestroy {
    LABELS;
    TEXTS;
    protected readonly ViewState = ViewState;

    protected translateAppliance = translateAppliance(this.translate);

    private readonly monthDateDisplayFormat = 'MMMM YYYY';
    private readonly yearDateDisplayFormat = 'YYYY';

    currentMode: string = AppliancesDetailDataMode.MONTH;
    currentDataDate = '';
    timeframeOffset = 0;
    interactionDisabled = true;
    currentNilmPhaseAllowsRetraining = false;
    isVisionUser = true;
    private _subscription: Subscription;
    isDataAvailable = true;

    appliancesDetailData$ = this.appliancesDetailDataProvider.appliancesDetailData$.pipe(
        tap((data) => {
            this.isDataAvailable = !(!data.categories.length && data.viewState === 'SUCCESS');
            this.interactionDisabled = false;
            this.cdRef.detectChanges();
        })
    );

    currentNilmPhaseAllowsRetraining$ = this.nilm
        .getNilmStatusData()
        .pipe(map((data) => data.nilmStatus === 3));

    enterRetraining$ = new Subject<boolean>();

    lottieConfig: AnimationOptions = {
        path: 'assets/anim/empty-states/animation_disaggregation_no_data.json',
        renderer: 'svg',
        autoplay: true,
        loop: true,
        name: 'Gerate ',
    };

    constructor(
        private nilm: NilmService,
        private profileUpdate: ProfileUpdateService,
        private appliancesDetailDataProvider: AppliancesDetailDataProviderService,
        private cdRef: ChangeDetectorRef,
        private initialization: InitializationService,
        private translate: TranslateService
    ) {
        this._subscription = appliancesDetailDataProvider.currentModeChange.subscribe((value) => {
            this.handlePositionUpdate(value);
        });
    }

    ngOnInit(): void {
        const currentLang = this.translate.currentLang;
        this.appliancesDetailDataProvider.getAppliancesDataForCurrentMode();
        this.checkIsVisionUser();

        this.currentDataDate = moment()
            .locale(currentLang)
            .subtract(this.timeframeOffset, 'months')
            .format(this.monthDateDisplayFormat);
        this.nilm
            .getNilmStatusData()
            .subscribe((data) => (this.currentNilmPhaseAllowsRetraining = data.nilmStatus === 3));
        this.translate
            .get('screens.dashboard.appliances.appliancesDetailTexts')
            .subscribe((texts) => {
                this.TEXTS = texts;
            });

        this.translate
            .get('screens.dashboard.appliances.appliancesDetailLabels')
            .subscribe((labels) => {
                this.LABELS = labels;
            });
    }

    checkIsVisionUser(): void {
        this.initialization.getWithCache().subscribe((data) => {
            if ('product_name' in data) {
                this.isVisionUser = data.product_name.toLowerCase().includes('vision');
            }
        });
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }

    /**
     * Skip a position forward until the logical end is reached
     */
    positionForward() {
        if (this.timeframeOffset > 0) {
            --this.timeframeOffset;
            this.handlePositionUpdate();
        }
    }

    /**
     * Skip a position back
     */
    positionBack() {
        ++this.timeframeOffset;
        this.handlePositionUpdate();
    }

    /**
     * Toggle an accordion element
     */
    toggleAccordion(element): void {
        element.accordionOpen = !element.accordionOpen;
    }

    /**
     * Determine the icon path for a category
     * @param category
     */
    determineCategoryImagePath(category: string): string {
        return `url(/assets/img/graphics/appliances/${category}.png)`;
    }

    /**
     * Determine the icon path for an appliance category
     */
    determineApplianceCategoryItemPath(iconName): string {
        return `url(/assets/img/icons/household/${iconName}.png)`;
    }

    /**
     * Handle Position update
     * @private
     */
    private handlePositionUpdate(mode?: string): void {
        if (mode) {
            this.currentMode = mode;
            this.appliancesDetailDataProvider.updateCurrentOffset(0);
            this.timeframeOffset = 0;
        } else {
            this.appliancesDetailDataProvider.updateCurrentOffset(this.timeframeOffset);
        }
        this.appliancesDetailDataProvider.updateCurrentOffset(this.timeframeOffset);
        this.appliancesDetailDataProvider.getAppliancesDataForCurrentMode();
        this.interactionDisabled = true;
        this.cdRef.detectChanges();
        const currentLang = this.translate.currentLang;

        switch (this.currentMode) {
            case AppliancesDetailDataMode.MONTH: {
                this.currentDataDate = moment()
                    .locale(currentLang)
                    .subtract(this.timeframeOffset, 'months')
                    .format(this.monthDateDisplayFormat);
                break;
            }
            case AppliancesDetailDataMode.YEAR: {
                this.currentDataDate = moment()
                    .locale(currentLang)
                    .subtract(this.timeframeOffset, 'years')
                    .format(this.yearDateDisplayFormat);
                break;
            }
        }
    }
}
